import React, { useEffect, useState } from 'react';
import { marketing, owner as ownerApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Bot, Loader2, Sparkles, RotateCcw, Save, ChevronDown } from 'lucide-react';
import { MarketingLocked } from './LandingPageBuilder';

// Wave C — Auto-Responder (owner view). All 19 triggers listed with an
// on/off toggle. Click a row to expand and edit the resolved template.
export default function AutoResponderPage() {
  const [rows, setRows] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [genBid, setGenBid] = useState('');
  const [locked, setLocked] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [openKey, setOpenKey] = useState(null);

  const load = async () => {
    try {
      const [tpls, biz] = await Promise.all([
        marketing.listAutoResponderTemplates(),
        ownerApi.myBusinesses().catch(() => []),
      ]);
      setRows(tpls || []);
      setBusinesses(biz || []);
      if (!genBid && biz?.length) setGenBid(biz[0].id);
    } catch (e) {
      if (e?.response?.status === 403) setLocked(true);
      else toast({ title: 'Failed to load auto-responder' });
      setRows([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = async (row) => {
    const next = !row.enabled;
    setRows(rows.map(r => r.key === row.key ? { ...r, enabled: next } : r));
    try { await marketing.toggleAutoResponder(row.key, next); }
    catch (e) {
      setRows(rows);
      toast({ title: 'Toggle failed', description: e?.response?.data?.detail || '' });
    }
  };

  const regenAll = async () => {
    if (!genBid) { toast({ title: 'Pick a business first' }); return; }
    setGenerating(true);
    try {
      const r = await marketing.generateAutoResponders(genBid);
      toast({ title: 'AI drafts ready', description: `${r.generated}/${r.total} templates personalized.` });
      await load();
    } catch (e) {
      toast({ title: 'Generate failed', description: e?.response?.data?.detail || '' });
    } finally { setGenerating(false); }
  };

  if (locked) return <MarketingLocked />;
  if (rows === null) return <div className="text-gray-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;

  const grouped = rows.reduce((acc, r) => { (acc[r.category] ||= []).push(r); return acc; }, {});
  const CATEGORY_ORDER = ['onboarding','sequence','engagement','loyalty','milestone','auction','events','purchases','re_engagement','personal'];
  const catLabel = { onboarding:'Onboarding', sequence:'Welcome Sequence', engagement:'Engagement', loyalty:'Loyalty', milestone:'Milestones', auction:'Auction', events:'Events', purchases:'Purchases', re_engagement:'Re-engagement', personal:'Birthday & Anniversary' };

  return (
    <div className="space-y-4" data-testid="auto-responder-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Bot size={22}/> Auto-Responder</h1>
          <p className="text-sm text-gray-400 mt-1">All 19 triggers run automatically from day one. AI writes the default copy from your business profile — you can override any message anytime.</p>
        </div>
      </div>

      {/* Regenerate all with AI */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 flex items-center gap-2 flex-wrap">
        <Sparkles size={16} className="text-green-400"/>
        <span className="text-white text-sm">Personalize every template with AI</span>
        <select data-testid="ar-gen-business" value={genBid} onChange={e => setGenBid(e.target.value)}
                className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-white text-sm">
          {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          {!businesses.length && <option value="">No businesses</option>}
        </select>
        <button data-testid="ar-generate-all-btn" onClick={regenAll} disabled={generating || !genBid}
                className="pbb-btn-green inline-flex items-center gap-1 text-sm disabled:opacity-60">
          {generating ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>}
          Generate all
        </button>
      </div>

      {CATEGORY_ORDER.filter(c => grouped[c]).map(cat => (
        <div key={cat} className="space-y-2">
          <div className="text-xs text-gray-500 uppercase tracking-wide">{catLabel[cat] || cat}</div>
          <div className="bg-gray-900 border border-gray-800 rounded-lg divide-y divide-gray-800">
            {grouped[cat].map(row => (
              <TriggerRow key={row.key} row={row} isOpen={openKey === row.key}
                            onToggleOpen={() => setOpenKey(openKey === row.key ? null : row.key)}
                            onToggleEnabled={() => toggle(row)}
                            onSaved={(fresh) => setRows(rows.map(r => r.key === row.key ? { ...r, ...fresh } : r))}/>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TriggerRow({ row, isOpen, onToggleOpen, onToggleEnabled, onSaved }) {
  const [form, setForm] = useState({ subject: row.subject, emailBody: row.emailBody, smsBody: row.smsBody });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm({ subject: row.subject, emailBody: row.emailBody, smsBody: row.smsBody }); }, [row.subject, row.emailBody, row.smsBody]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await marketing.putAutoResponderTemplate(row.key, form);
      onSaved(r);
      toast({ title: `Saved "${row.label}"` });
    } catch (e) { toast({ title: 'Save failed', description: e?.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm(`Reset "${row.label}" to the platform default?`)) return;
    try {
      const r = await marketing.deleteAutoResponderTemplate(row.key);
      onSaved(r);
      toast({ title: 'Reset to platform default' });
    } catch (e) { toast({ title: 'Reset failed' }); }
  };

  return (
    <div className="p-3" data-testid={`ar-row-${row.key}`}>
      <div className="flex items-center gap-3">
        <button onClick={onToggleOpen} data-testid={`ar-row-expand-${row.key}`}
                className="flex-1 min-w-0 flex items-center gap-2 text-left">
          <ChevronDown size={14} className={`text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}/>
          <div className="min-w-0">
            <div className="text-white text-sm font-medium truncate">{row.label}</div>
            <div className="text-xs text-gray-500 truncate">{row.subject || '(no subject)'}</div>
          </div>
        </button>
        {row.sequenceDelayHours ? (
          <span className="text-[10px] text-gray-400 border border-gray-700 rounded-full px-2 py-0.5 whitespace-nowrap">
            +{row.sequenceDelayHours}h
          </span>
        ) : null}
        {row.transactional && (
          <span className="text-[10px] text-blue-300 border border-blue-500/40 bg-blue-500/10 rounded-full px-2 py-0.5 whitespace-nowrap">
            Transactional
          </span>
        )}
        <label className="inline-flex items-center gap-1 text-xs cursor-pointer whitespace-nowrap">
          <input data-testid={`ar-toggle-${row.key}`} type="checkbox" checked={row.enabled} onChange={onToggleEnabled}/>
          <span className={row.enabled ? 'text-green-400' : 'text-gray-500'}>{row.enabled ? 'On' : 'Off'}</span>
        </label>
      </div>
      {isOpen && (
        <div className="mt-3 space-y-2 pl-6">
          <div>
            <label className="block text-[10px] text-gray-500 mb-1 uppercase tracking-wide">Email subject</label>
            <input data-testid={`ar-form-subject-${row.key}`} value={form.subject || ''}
                   onChange={e => setForm({ ...form, subject: e.target.value })}
                   className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm"/>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1 uppercase tracking-wide">Email body (HTML allowed)</label>
            <textarea data-testid={`ar-form-emailBody-${row.key}`} rows={5} value={form.emailBody || ''}
                      onChange={e => setForm({ ...form, emailBody: e.target.value })}
                      className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm font-mono"/>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1 uppercase tracking-wide">SMS body <span className="text-gray-600">({(form.smsBody||'').length}/240)</span></label>
            <textarea data-testid={`ar-form-smsBody-${row.key}`} rows={2} maxLength={240} value={form.smsBody || ''}
                      onChange={e => setForm({ ...form, smsBody: e.target.value })}
                      className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm"/>
          </div>
          <div className="text-[11px] text-gray-500">Placeholders: <code>{'{name}'}</code>, <code>{'{business_name}'}</code>, <code>{'{points}'}</code>, <code>{'{item_title}'}</code>, <code>{'{event_title}'}</code></div>
          <div className="flex gap-2">
            <button data-testid={`ar-save-${row.key}`} onClick={save} disabled={saving}
                    className="pbb-btn-green inline-flex items-center gap-1 text-xs disabled:opacity-60">
              {saving ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Save
            </button>
            <button data-testid={`ar-reset-${row.key}`} onClick={reset}
                    className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-700 text-white hover:bg-gray-800">
              <RotateCcw size={12}/> Reset to default
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
